/*
 * Homeline glass header (Oct 2026 "Glass Header, Smart Search & Favourites"
 * handoff, option 3b). Markup: sections/header.liquid. Styles: hl-header.css.
 *
 *   Marquee      one message at a time, every N seconds, with clickable dots
 *   Scroll       full near the top, compact after 24px down, full again after
 *                8px up, wherever the visitor is
 *   Dropdowns    one shared glass panel that slides between nav items
 *   Search       the field opens a stage inside the header (desktop) or a
 *                full-screen sheet (mobile), fed by /search/suggest.json and
 *                three layers that make sure a search never ends on nothing:
 *                  1. typo tolerance + synonyms ("Showing results for …")
 *                  2. intent shortcuts (warranty, careers, trade, …) from
 *                     assets/search-intents.json
 *                  3. a fallback of popular pieces, trending searches and a
 *                     contact card
 *                Every settled query is published as an `hl_search` analytics
 *                event so the synonym and intent lists can be tuned.
 *
 * <sticky-header> stays the element name: cart-notification.js, media-gallery
 * .js and quick-order-list.js look the header up by it.
 */
(function () {
  'use strict';

  var MOBILE = '(max-width: 960px)';
  var VIEWED_KEY = 'hl_recently_viewed_v1';
  var INDEX_KEY = 'hlh_search_index_v1';
  var EASE_MS = 460;

  /* ─────────────── small helpers ─────────────── */

  function isMobile() {
    return window.matchMedia(MOBILE).matches;
  }

  function reduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function sized(url, w) {
    if (!url || url.indexOf('data:') === 0) return url || '';
    if (url.indexOf('//') === 0) url = 'https:' + url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  var ICONS = {
    search: '<circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>',
    'arrow-up-right': '<path d="M7 7h10v10"/><path d="M7 17 17 7"/>',
    'arrow-right': '<path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>',
    shield: '<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/>',
    briefcase: '<path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/><rect width="20" height="14" x="2" y="6" rx="2"/>',
    building: '<rect width="16" height="20" x="4" y="2" rx="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M12 14h.01"/><path d="M16 10h.01"/><path d="M16 14h.01"/><path d="M8 10h.01"/><path d="M8 14h.01"/>',
    truck: '<path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/>',
    'return': '<path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/>',
    sparkles: '<path d="M9.94 15.5A2 2 0 0 0 8.5 14.06l-6.14-1.58a.5.5 0 0 1 0-.96L8.5 9.94A2 2 0 0 0 9.94 8.5l1.58-6.14a.5.5 0 0 1 .96 0L14.06 8.5A2 2 0 0 0 15.5 9.94l6.14 1.58a.5.5 0 0 1 0 .96L15.5 14.06a2 2 0 0 0-1.44 1.44l-1.58 6.14a.5.5 0 0 1-.96 0z"/><path d="M20 3v4"/><path d="M22 5h-4"/>',
    message: '<path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/>',
    pin: '<path d="M20 10c0 4.99-5.54 10.19-7.4 11.79a1 1 0 0 1-1.2 0C9.54 20.19 4 14.99 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/>',
    info: '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>',
    trash: '<path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>',
    ruler: '<path d="M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L2.7 8.7a2.41 2.41 0 0 1 0-3.4l2.6-2.6a2.41 2.41 0 0 1 3.4 0Z"/><path d="m14.5 12.5 2-2"/><path d="m11.5 9.5 2-2"/><path d="m8.5 6.5 2-2"/><path d="m17.5 15.5 2-2"/>',
    receipt: '<path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z"/><path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"/><path d="M12 17.5v-11"/>',
    'package': '<path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"/><path d="M12 22V12"/><path d="m3.3 7 8.7 5 8.7-5"/>'
  };

  function icon(name, size, stroke) {
    var s = size || 16;
    var span = document.createElement('span');
    span.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="' + s + '" height="' + s + '" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="' +
      (stroke || 2) + '" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' + (ICONS[name] || ICONS.info) + '</svg>';
    return span.firstChild;
  }

  // Wraps every occurrence of `q` in `text` in the orange bold mark, built from
  // text nodes so a product title can never inject markup.
  function highlight(text, q) {
    var frag = document.createDocumentFragment();
    text = text || '';
    q = (q || '').trim();
    if (!q) {
      frag.appendChild(document.createTextNode(text));
      return frag;
    }
    var lower = text.toLowerCase();
    var needle = q.toLowerCase();
    var i = 0;
    while (i < text.length) {
      var at = lower.indexOf(needle, i);
      if (at === -1) {
        frag.appendChild(document.createTextNode(text.slice(i)));
        break;
      }
      if (at > i) frag.appendChild(document.createTextNode(text.slice(i, at)));
      frag.appendChild(el('b', 'hlh-m', text.slice(at, at + needle.length)));
      i = at + needle.length;
    }
    return frag;
  }

  /* Search intelligence lives in assets/hl-search-brain.js (window.HLSearchBrain),
     shared with the tab bar's search and the catalogue. */
  var BRAIN = window.HLSearchBrain;

  function normalise(raw) {
    return BRAIN ? BRAIN.normalise(raw) : String(raw || '').toLowerCase().trim();
  }

  /* ─────────────── recently viewed ─────────────── */

  function readViewed() {
    try {
      var list = JSON.parse(localStorage.getItem(VIEWED_KEY) || '[]');
      return Array.isArray(list) ? list.filter(function (p) { return p && p.handle; }) : [];
    } catch (e) {
      return [];
    }
  }

  function recordViewed(p) {
    if (!p || !p.handle) return;
    var list = readViewed().filter(function (x) {
      return x.handle !== p.handle;
    });
    list.unshift(p);
    try {
      localStorage.setItem(VIEWED_KEY, JSON.stringify(list.slice(0, 8)));
    } catch (e) {}
  }

  /* ─────────────── analytics ─────────────── */

  function publish(entry) {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish('hl_search', entry);
      }
    } catch (e) {}
    try {
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push(Object.assign({ event: 'hl_search' }, entry));
      }
    } catch (e) {}
  }

  /* ─────────────── the header ─────────────── */

  class HlHeader extends HTMLElement {
    connectedCallback() {
      if (this.wired) return;
      this.wired = true;

      this.bar = this.querySelector('.hlh__bar');
      this.panel = this.querySelector('.hlh__panel');
      this.row2 = this.querySelector('[data-hlh-row2]');
      this.stage = this.querySelector('[data-hlh-stage]');
      this.stageInner = this.querySelector('[data-hlh-stage-inner]');
      this.form = this.querySelector('[data-hlh-form]');
      this.input = this.querySelector('[data-hlh-input]');
      this.clearBtn = this.querySelector('[data-hlh-clear]');

      var section = this.closest('.shopify-section') || document;
      this.scrim = section.querySelector('[data-hlh-scrim]');
      this.sheet = section.querySelector('[data-hlh-sheet]');
      this.sheetInner = section.querySelector('[data-hlh-sheet-inner]');

      this.data = { trending: [], popular: [] };
      var json = section.querySelector('[data-hlh-data]');
      if (json) {
        try {
          this.data = JSON.parse(json.textContent);
        } catch (e) {}
      }
      this.data.trending = (this.data.trending || [])
        .map(function (t) {
          return String(t).trim();
        })
        .filter(Boolean);
      if (this.data.viewed) recordViewed(this.data.viewed);


      this.setupMarquee();
      this.setupScroll();
      this.setupDropdowns();
      this.setupSearch();

      this.publishHeight();
      this.onResize = this.trackHeight.bind(this, EASE_MS);
      window.addEventListener('resize', this.onResize, { passive: true });
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(this.publishHeight.bind(this));
      }
    }

    disconnectedCallback() {
      window.clearInterval(this.marqueeTimer);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('keydown', this.onDocKey);
      this.unlockScroll();
      this.wired = false;
    }

    /* ---------- marquee ---------- */

    setupMarquee() {
      var self = this;
      this.msgs = Array.prototype.slice.call(this.querySelectorAll('[data-hlh-msg]'));
      this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-hlh-dot]'));
      this.mi = 0;
      if (this.msgs.length < 2) return;

      var speed = (parseFloat(this.dataset.speed) || 3.8) * 1000;
      var start = function () {
        window.clearInterval(self.marqueeTimer);
        self.marqueeTimer = window.setInterval(function () {
          self.showMessage((self.mi + 1) % self.msgs.length);
        }, speed);
      };

      this.dots.forEach(function (dot) {
        dot.addEventListener('click', function () {
          self.showMessage(parseInt(dot.dataset.hlhDot, 10) || 0);
          start();
        });
      });

      // Pausing while a pointer rests on the bar lets a message be read.
      if (this.bar) {
        this.bar.addEventListener('pointerenter', function () {
          window.clearInterval(self.marqueeTimer);
        });
        this.bar.addEventListener('pointerleave', start);
      }
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) window.clearInterval(self.marqueeTimer);
        else start();
      });
      start();
    }

    showMessage(i) {
      if (i === this.mi || !this.msgs[i]) return;
      var out = this.msgs[this.mi];
      var next = this.msgs[i];
      var soft = reduced();

      out.classList.remove('is-on');
      out.setAttribute('aria-hidden', 'true');
      if (!soft) {
        out.classList.add('is-out');
        window.setTimeout(function () {
          out.classList.remove('is-out');
        }, 420);
      }
      next.classList.remove('is-out');
      next.classList.add('is-on');
      next.removeAttribute('aria-hidden');

      this.dots.forEach(function (d, n) {
        d.classList.toggle('is-on', n === i);
        if (n === i) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });
      this.mi = i;
    }

    /* ---------- scroll ---------- */

    setupScroll() {
      var self = this;
      this.mode = 'full';
      this.lastY = window.pageYOffset || 0;
      this.anchorY = this.lastY;
      this.ticking = false;

      this.onScroll = function () {
        if (self.ticking) return;
        self.ticking = true;
        window.requestAnimationFrame(function () {
          self.ticking = false;
          self.updateScroll();
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.updateScroll();
    }

    updateScroll() {
      var y = Math.max(0, window.pageYOffset || document.documentElement.scrollTop || 0);
      var d = y - this.lastY;
      var mode = this.mode;

      // The anchor sits at the last turning point. Going down, it is pinned
      // while full and follows while compact; going up, the reverse — so the
      // 24px and the 8px are always measured from where the direction changed.
      if (y < 60) {
        mode = 'full';
        this.anchorY = y;
      } else if (d > 0) {
        if (mode !== 'compact' && y - this.anchorY > 24) mode = 'compact';
        if (mode === 'compact') this.anchorY = y;
      } else if (d < 0) {
        if (mode === 'compact' && this.anchorY - y > 8) mode = 'full';
        if (mode !== 'compact') this.anchorY = y;
      }
      this.lastY = y;

      if (mode !== this.mode) {
        this.mode = mode;
        this.applyMode();
      }
    }

    applyMode() {
      var compact = this.mode === 'compact' && !this.searching;
      if (compact === this.classList.contains('is-compact')) return;
      this.classList.toggle('is-compact', compact);
      this.querySelectorAll('[data-hlh-inline] a').forEach(function (a) {
        a.tabIndex = compact ? 0 : -1;
      });
      if (compact) this.closeMenu();
      this.trackHeight(EASE_MS);
    }

    // --hl-header-h is the header's live height without the search stage:
    // the catalogue's filter bar sticks under it. hl:header-height announces
    // each new value for consumers that cannot read a custom property late.
    publishHeight() {
      var h = this.getBoundingClientRect().height;
      if (this.stage) h -= this.stage.getBoundingClientRect().height;
      h = Math.ceil(h);
      if (h > 0 && h !== this.lastHeight) {
        this.lastHeight = h;
        document.documentElement.style.setProperty('--hl-header-h', h + 'px');
        document.dispatchEvent(new CustomEvent('hl:header-height', { detail: h }));
      }
      if (this.sheet) {
        var top = this.bar ? Math.round(this.bar.getBoundingClientRect().bottom) : 0;
        this.sheet.style.setProperty('--hlh-sheet-top', top + 'px');
      }
    }

    trackHeight(ms) {
      var self = this;
      var until = performance.now() + (ms || EASE_MS);
      if (this.trackUntil) {
        this.trackUntil = until;
        return;
      }
      this.trackUntil = until;
      var step = function () {
        self.publishHeight();
        if (performance.now() < self.trackUntil) window.requestAnimationFrame(step);
        else self.trackUntil = 0;
      };
      window.requestAnimationFrame(step);
    }

    /* ---------- dropdowns ---------- */

    setupDropdowns() {
      var self = this;
      this.nav = this.querySelector('[data-hlh-nav]');
      this.dd = this.querySelector('[data-hlh-dd]');
      this.ddInner = this.querySelector('[data-hlh-dd-inner]');
      if (!this.nav || !this.dd) return;
      this.dd.hidden = false;
      this.openTrigger = null;

      var links = Array.prototype.slice.call(this.nav.querySelectorAll('.hlh__nav-link'));

      links.forEach(function (link) {
        var hasMenu = link.hasAttribute('data-hlh-trigger');

        link.addEventListener('pointerenter', function (e) {
          if (e.pointerType === 'touch') return;
          window.clearTimeout(self.closeTimer);
          window.clearTimeout(self.openTimer);
          if (!hasMenu) {
            self.closeMenu();
            return;
          }
          if (self.openTrigger) self.openMenu(link);
          else {
            self.openTimer = window.setTimeout(function () {
              self.openMenu(link);
            }, 90);
          }
        });

        if (!hasMenu) return;

        // On touch the first tap opens the menu; "Shop all" inside it goes on.
        link.addEventListener('click', function (e) {
          if (self.lastPointer === 'touch' && self.openTrigger !== link) {
            e.preventDefault();
            self.openMenu(link);
          }
        });

        link.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
            e.preventDefault();
            self.openMenu(link, true);
            var first = self.dd.querySelector('a');
            if (first) first.focus();
          } else if (e.key === 'Escape' && self.openTrigger) {
            e.preventDefault();
            self.closeMenu();
          }
        });
      });

      this.nav.addEventListener('pointerdown', function (e) {
        self.lastPointer = e.pointerType;
      });

      this.nav.addEventListener('pointerenter', function () {
        window.clearTimeout(self.closeTimer);
      });

      this.nav.addEventListener('pointerleave', function (e) {
        if (e.pointerType === 'touch') return;
        window.clearTimeout(self.openTimer);
        self.closeTimer = window.setTimeout(function () {
          self.closeMenu();
        }, 140);
      });

      this.dd.addEventListener('keydown', function (e) {
        var items = Array.prototype.slice.call(self.dd.querySelectorAll('a'));
        var at = items.indexOf(document.activeElement);
        if (e.key === 'Escape') {
          e.preventDefault();
          var trigger = self.openTrigger;
          self.closeMenu();
          if (trigger) trigger.focus();
        } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          var step = e.key === 'ArrowDown' ? 1 : -1;
          var next = items[(at + step + items.length) % items.length];
          if (next) next.focus();
        }
      });

      // Tabbing out of the nav, or tapping elsewhere, closes it.
      this.nav.addEventListener('focusout', function (e) {
        if (!e.relatedTarget || !self.nav.contains(e.relatedTarget)) {
          window.setTimeout(function () {
            if (!self.nav.contains(document.activeElement)) self.closeMenu();
          }, 0);
        }
      });
      document.addEventListener('pointerdown', function (e) {
        if (self.openTrigger && !self.nav.contains(e.target)) self.closeMenu();
      });
    }

    openMenu(link) {
      var tpl = document.getElementById(link.getAttribute('data-hlh-trigger'));
      if (!tpl || this.searching) return;
      var wasOpen = !!this.openTrigger;

      if (this.openTrigger !== link) {
        this.ddInner.textContent = '';
        this.ddInner.appendChild(tpl.content.cloneNode(true));
        this.ddInner.querySelectorAll('a').forEach(function (a) {
          a.tabIndex = -1;
        });
      }

      var w = parseInt(tpl.dataset.width, 10) || 240;
      var nr = this.nav.getBoundingClientRect();
      var r = link.getBoundingClientRect();
      w = Math.min(w, nr.width);
      var left = Math.max(0, Math.min(nr.width - w, r.left - nr.left + r.width / 2 - w / 2));

      // A panel opening from closed starts under its own item; only a panel
      // that is already open slides to the next one.
      if (!wasOpen) {
        this.dd.classList.add('is-instant');
        this.dd.style.left = left + 'px';
        this.dd.style.width = w + 'px';
        void this.dd.offsetWidth;
        this.dd.classList.remove('is-instant');
      } else {
        this.dd.style.left = left + 'px';
        this.dd.style.width = w + 'px';
      }

      if (this.openTrigger && this.openTrigger !== link) {
        this.openTrigger.classList.remove('is-open');
        this.openTrigger.setAttribute('aria-expanded', 'false');
      }
      this.openTrigger = link;
      link.classList.add('is-open');
      link.setAttribute('aria-expanded', 'true');
      this.classList.add('is-dd');
    }

    closeMenu() {
      window.clearTimeout(this.openTimer);
      if (!this.openTrigger) return;
      this.openTrigger.classList.remove('is-open');
      this.openTrigger.setAttribute('aria-expanded', 'false');
      this.openTrigger = null;
      this.classList.remove('is-dd');
    }

    /* ---------- search ---------- */

    setupSearch() {
      var self = this;
      if (!this.input) return;
      this.searching = false;
      this.state = { q: '' };
      this.seq = 0;
      this.sel = -1;

      this.input.addEventListener('focus', function () {
        self.openSearch();
      });

      this.input.addEventListener('input', function () {
        self.syncClear();
        self.query(self.input.value);
      });

      this.input.addEventListener('keydown', function (e) {
        self.onKey(e);
      });

      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', function () {
          self.input.value = '';
          self.syncClear();
          self.query('');
          self.input.focus();
        });
      }

      Array.prototype.forEach.call(this.querySelectorAll('[data-hlh-close]'), function (b) {
        b.addEventListener('pointerdown', function (e) {
          // Keep the field from blurring first, which would close twice.
          e.preventDefault();
        });
        b.addEventListener('click', function (e) {
          e.preventDefault();
          self.closeSearch();
        });
      });

      if (this.scrim) {
        this.scrim.addEventListener('click', function () {
          self.closeSearch();
        });
      }

      if (this.form) {
        this.form.addEventListener('submit', function () {
          var q = self.effectiveQuery();
          if (q && q !== self.input.value.trim()) self.input.value = q;
          self.log('submit');
        });
      }

      // Blur closes, but only when focus actually goes somewhere else on the
      // page. A click on empty space inside the stage, or switching windows,
      // leaves the search open.
      var inside = function (n) {
        return n && (self.contains(n) || (self.sheet && self.sheet.contains(n)));
      };
      this.addEventListener('focusout', function (e) {
        if (self.searching && e.relatedTarget && !inside(e.relatedTarget)) self.closeSearch(true);
      });
      if (this.sheet) {
        this.sheet.addEventListener('focusout', function (e) {
          if (self.searching && e.relatedTarget && !inside(e.relatedTarget)) self.closeSearch(true);
        });
      }

      this.onDocKey = function (e) {
        if (e.key === 'Escape' && self.searching) {
          e.preventDefault();
          self.closeSearch();
        }
      };
      document.addEventListener('keydown', this.onDocKey);

      // Leaving the breakpoint mid-search would strand the wrong surface.
      window.matchMedia(MOBILE).addEventListener('change', function () {
        if (self.searching) self.closeSearch();
      });

      this.syncClear();
    }

    syncClear() {
      var has = this.input.value.trim() !== '';
      if (this.clearBtn) this.clearBtn.hidden = !has;
      this.classList.toggle('has-query', has);
    }

    openSearch() {
      if (this.searching) return;
      this.searching = true;
      this.closeMenu();
      this.classList.add('is-search');
      this.applyMode();
      this.input.setAttribute('aria-expanded', 'true');
      if (isMobile()) {
        this.lockScroll();
        if (this.sheet) this.sheet.classList.add('is-on');
      } else if (this.scrim) {
        this.scrim.classList.add('is-on');
      }
      this.trackHeight(EASE_MS);
      // Warm both data sources while the stage is still opening.
      if (BRAIN) {
        BRAIN.load();
        BRAIN.loadIndex();
      }
      this.query(this.input.value, true);
    }

    closeSearch(keepFocus) {
      if (!this.searching) return;
      this.searching = false;
      window.clearTimeout(this.timer);
      this.seq++;
      this.classList.remove('is-search');
      if (this.scrim) this.scrim.classList.remove('is-on');
      if (this.sheet) this.sheet.classList.remove('is-on');
      this.unlockScroll();
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      // Closing clears the query, as the handoff asks, and gives the field up.
      this.input.value = '';
      this.syncClear();
      this.state = { q: '' };
      if (!keepFocus && document.activeElement === this.input) this.input.blur();
      this.applyMode();
      this.trackHeight(EASE_MS);
    }

    lockScroll() {
      if (this.locked) return;
      this.locked = true;
      document.documentElement.style.overflow = 'hidden';
    }

    unlockScroll() {
      if (!this.locked) return;
      this.locked = false;
      document.documentElement.style.overflow = '';
    }

    effectiveQuery() {
      var s = this.state || {};
      var typed = (this.input.value || '').trim();
      // The state lags the field by the debounce plus the fetch, so a quick
      // Enter after typing would otherwise submit the half-typed word. Only
      // trust the correction when it was worked out for what's in the field.
      if (s.q !== typed) return typed;
      return (s.corrected && !s.noCorrect ? s.corrected : typed).trim();
    }

    query(raw, immediate) {
      var self = this;
      var q = (raw || '').trim();
      window.clearTimeout(this.timer);
      window.clearTimeout(this.logTimer);
      if (!q) {
        this.seq++;
        this.state = { q: '' };
        this.render();
        return;
      }
      this.timer = window.setTimeout(function () {
        self.run(q, {});
      }, immediate ? 0 : 140);
    }

    run(raw, opts) {
      var self = this;
      var token = ++this.seq;
      if (!BRAIN) return;

      BRAIN.search(raw, {
        suggestUrl: this.dataset.suggestUrl,
        noCorrect: !!opts.noCorrect,
        limit: 6,
        intents: 3
      }).then(function (r) {
        if (!r || token !== self.seq) return;
        self.state = {
          q: raw,
          corrected: r.corrected,
          typo: r.typo,
          noCorrect: !!opts.noCorrect,
          intents: r.intents,
          suggestions: r.suggestions,
          pages: r.pages,
          products: r.products,
          local: r.local,
          nothing: r.nothing
        };
        self.render();

        window.clearTimeout(self.logTimer);
        self.logTimer = window.setTimeout(function () {
          self.log('settled');
        }, 1200);
      });
    }

    log(kind) {
      var s = this.state || {};
      if (!s.q) return;
      var key = kind + '|' + s.q;
      if (key === this.lastLogged) return;
      this.lastLogged = key;
      publish({
        kind: kind,
        query: s.q,
        normalised: normalise(s.q),
        corrected: s.corrected || null,
        typo: !!s.typo,
        intents: (s.intents || []).map(function (i) {
          return i.id;
        }),
        products: (s.products || []).length,
        suggestions: (s.suggestions || []).length,
        fallback: !!s.nothing
      });
    }

    /* ---------- rendering ---------- */

    viewed() {
      var current = this.data.viewed && this.data.viewed.handle;
      return readViewed().filter(function (p) {
        return p.handle !== current;
      });
    }

    render() {
      var mobile = isMobile();
      var host = mobile ? this.sheetInner : this.stageInner;
      if (!host) return;
      var s = this.state || { q: '' };
      host.textContent = '';
      if (mobile) this.renderMobile(host, s);
      else this.renderDesktop(host, s);

      this.rows = Array.prototype.slice.call(host.querySelectorAll('[data-sel]'));
      this.rows.forEach(function (row, i) {
        row.id = 'HeaderSearchRow-' + i;
      });
      this.setSel(-1);
      var self = this;
      this.rows.forEach(function (row, i) {
        row.addEventListener('pointerenter', function () {
          self.setSel(i);
        });
        row.addEventListener('click', function () {
          if (row.tagName === 'A') self.log('select');
        });
      });
    }

    setSel(i) {
      this.sel = i;
      (this.rows || []).forEach(function (row, n) {
        row.classList.toggle('is-sel', n === i);
      });
      var row = this.rows && this.rows[i];
      if (row) {
        this.input.setAttribute('aria-activedescendant', row.id);
        if (row.scrollIntoView) row.scrollIntoView({ block: 'nearest' });
      } else {
        this.input.removeAttribute('aria-activedescendant');
      }
    }

    onKey(e) {
      var rows = this.rows || [];
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (!rows.length) return;
        e.preventDefault();
        var step = e.key === 'ArrowDown' ? 1 : -1;
        var next = this.sel === -1 && step < 0 ? rows.length - 1 : (this.sel + step + rows.length) % rows.length;
        this.setSel(next);
      } else if (e.key === 'Enter') {
        var row = rows[this.sel];
        if (!row) return; // the form submits: Enter searches everything
        e.preventDefault();
        if (row.tagName === 'A') {
          this.log('select');
          window.location.href = row.href;
        } else {
          row.click();
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this.closeSearch();
      }
    }

    pick(term) {
      this.input.value = term;
      this.syncClear();
      this.input.focus();
      this.query(term, true);
    }

    searchUrl(q) {
      var base = this.dataset.searchUrl || '/collections/all';
      return base + (base.indexOf('?') === -1 ? '?' : '&') + 'q=' + encodeURIComponent(q);
    }

    // -- building blocks --

    eyebrow(text) {
      return el('p', 'hlh-eyebrow', text);
    }

    didYouMean(s) {
      var self = this;
      if (!s.corrected || !s.typo) return null;
      var p = el('p', 'hlh-dym');
      p.appendChild(document.createTextNode('Showing results for '));
      var fix = el('button', 'hlh-dym__fix', s.corrected);
      fix.type = 'button';
      fix.addEventListener('click', function () {
        self.pick(s.corrected);
      });
      p.appendChild(fix);
      p.appendChild(document.createTextNode('. Search instead for '));
      var orig = el('button', '', s.q);
      orig.type = 'button';
      orig.addEventListener('click', function () {
        self.input.focus();
        self.run(s.q, { noCorrect: true });
      });
      p.appendChild(orig);
      return p;
    }

    intentCards(list) {
      if (!list || !list.length) return null;
      var wrap = el('div', 'hlh-intents');
      list.forEach(function (it) {
        var a = el('a', 'hlh-intent');
        a.href = it.url;
        a.setAttribute('data-sel', '');
        var ic = el('span', 'hlh-intent__ic');
        ic.appendChild(icon(it.icon, 20, 1.75));
        a.appendChild(ic);
        var text = el('span', 'hlh-intent__text');
        text.appendChild(el('span', 'hlh-intent__title', it.title));
        if (it.description) text.appendChild(el('span', 'hlh-intent__desc', it.description));
        a.appendChild(text);
        a.appendChild(icon('arrow-right', 16, 2));
        wrap.appendChild(a);
      });
      return wrap;
    }

    // Intent cards first, then any store pages Shopify found (Delivery,
    // Warranty, …), shown the same way so a page is one tap away.
    shortcuts(s) {
      var pages = (s.pages || []).map(function (pg) {
        return { id: 'page', icon: 'info', title: pg.title, description: '', url: pg.url };
      });
      return (s.intents || []).concat(pages).slice(0, 4);
    }

    trendingRows() {
      var self = this;
      var list = el('div', 'hlh-trend');
      this.data.trending.forEach(function (t) {
        var b = el('button', 'hlh-trend__row');
        b.type = 'button';
        b.setAttribute('data-sel', '');
        b.appendChild(el('span', '', t));
        b.appendChild(icon('arrow-up-right', 16, 2));
        b.addEventListener('click', function () {
          self.pick(t);
        });
        list.appendChild(b);
      });
      return list;
    }

    chips() {
      var self = this;
      var wrap = el('div', 'hlh-chips');
      this.data.trending.forEach(function (t) {
        var b = el('button', 'hlh-chip', t);
        b.type = 'button';
        b.addEventListener('click', function () {
          self.pick(t);
        });
        wrap.appendChild(b);
      });
      return wrap;
    }

    suggestionRows(list, q, withIcon) {
      var self = this;
      var wrap = el('div', 'hlh-sugg');
      list.forEach(function (sg) {
        var a = el('a', 'hlh-sugg__row');
        a.href = sg.url && sg.url.indexOf('/collections/') !== -1 ? sg.url : self.searchUrl(sg.text);
        a.setAttribute('data-sel', '');
        if (withIcon) a.appendChild(icon('search', 16, 2));
        var t = el('span');
        t.appendChild(highlight(sg.text, q));
        a.appendChild(t);
        wrap.appendChild(a);
      });
      return wrap;
    }

    cards(list, q) {
      var wrap = el('div', 'hlh-cards');
      list.slice(0, 4).forEach(function (p) {
        var a = el('a', 'hlh-card');
        a.href = p.url;
        a.setAttribute('data-sel', '');
        var img = el('img', 'hlh-card__img');
        img.alt = '';
        img.loading = 'lazy';
        if (p.image) img.src = sized(p.image, 480);
        a.appendChild(img);
        var name = el('span', 'hlh-card__name');
        name.appendChild(highlight(p.title, q));
        a.appendChild(name);
        a.appendChild(el('span', 'hlh-card__price', p.price));
        wrap.appendChild(a);
      });
      return wrap;
    }

    listRows(list, q, lined) {
      var wrap = el('div', 'hlh-list' + (lined ? ' hlh-list--lined' : ''));
      list.forEach(function (p) {
        var a = el('a', 'hlh-list__row');
        a.href = p.url;
        a.setAttribute('data-sel', '');
        var img = el('img', 'hlh-list__img');
        img.alt = '';
        img.loading = 'lazy';
        if (p.image) img.src = sized(p.image, 160);
        a.appendChild(img);
        var text = el('span', 'hlh-list__text');
        var name = el('span', 'hlh-list__name');
        name.appendChild(highlight(p.title, q));
        text.appendChild(name);
        if (!lined && p.type) text.appendChild(el('span', 'hlh-list__variant', p.type));
        a.appendChild(text);
        a.appendChild(el('span', 'hlh-list__price', p.price));
        wrap.appendChild(a);
      });
      return wrap;
    }

    col(title) {
      var c = el('div', 'hlh-col');
      if (title) c.appendChild(this.eyebrow(title));
      return c;
    }

    nothingNote(q) {
      return el('p', 'hlh-note', 'Nothing found for “' + q + '”. Try a room or a material.');
    }

    // -- desktop stage --

    renderDesktop(host, s) {
      var q = (s.q || '').trim();
      var hq = s.corrected && !s.noCorrect ? s.corrected : q;
      var popular = this.data.popular || [];

      if (!q) {
        var left = this.col('Trending');
        left.appendChild(this.trendingRows());
        host.appendChild(left);

        var viewed = this.viewed();
        var right = this.col(viewed.length ? 'Recently viewed' : 'Popular pieces');
        var fill = viewed.concat(popular.filter(function (p) {
          return !viewed.some(function (v) { return v.handle === p.handle; });
        }));
        right.appendChild(this.cards(fill, ''));
        host.appendChild(right);
        return;
      }

      var dym = this.didYouMean(s);
      if (dym) host.appendChild(dym);
      var intents = this.intentCards(this.shortcuts(s));
      if (intents) host.appendChild(intents);

      var l = this.col('Suggestions');
      if (s.suggestions && s.suggestions.length) l.appendChild(this.suggestionRows(s.suggestions, hq));
      if (!s.products || !s.products.length) {
        if ((!s.suggestions || !s.suggestions.length) && !this.shortcuts(s).length) l.appendChild(this.nothingNote(q));
        l.appendChild(this.chips());
      }
      host.appendChild(l);

      var r;
      if (s.products && s.products.length) {
        r = this.col('Pieces');
        r.appendChild(this.cards(s.products, hq));
      } else {
        r = this.col('Popular pieces');
        r.appendChild(this.cards(popular, ''));
      }
      host.appendChild(r);

      if (s.nothing) host.appendChild(this.contactCard());
    }

    contactCard() {
      var fb = (BRAIN && BRAIN.fallback()) || {
        id: 'contact-fallback',
        icon: 'message',
        title: "Can't find it? Ask us",
        description: "Tell us what you're after and we'll help you find it.",
        url: '/pages/contact'
      };
      return this.intentCards([fb]);
    }

    // -- mobile sheet --

    renderMobile(host, s) {
      var q = (s.q || '').trim();
      var hq = s.corrected && !s.noCorrect ? s.corrected : q;
      var popular = this.data.popular || [];

      if (!q) {
        var t = this.col('Trending');
        t.appendChild(this.chips());
        host.appendChild(t);

        var viewed = this.viewed();
        var list = viewed.length ? viewed : popular;
        if (list.length) {
          var r = this.col(viewed.length ? 'Recently viewed' : 'Popular pieces');
          r.appendChild(this.listRows(list.slice(0, 4), '', true));
          host.appendChild(r);
        }
        return;
      }

      var dym = this.didYouMean(s);
      if (dym) host.appendChild(dym);
      var intents = this.intentCards(this.shortcuts(s));
      if (intents) host.appendChild(intents);

      if (s.suggestions && s.suggestions.length) host.appendChild(this.suggestionRows(s.suggestions, hq, true));

      if (s.products && s.products.length) {
        var p = this.col('Pieces');
        p.appendChild(this.listRows(s.products, hq, false));
        host.appendChild(p);
        return;
      }

      if ((!s.suggestions || !s.suggestions.length) && !this.shortcuts(s).length) host.appendChild(this.nothingNote(q));
      var pop = this.col('Popular pieces');
      pop.appendChild(this.listRows(popular.slice(0, 4), '', false));
      host.appendChild(pop);
      var tr = this.col('Trending');
      tr.appendChild(this.chips());
      host.appendChild(tr);
      if (s.nothing) host.appendChild(this.contactCard());
    }
  }

  if (!customElements.get('sticky-header')) {
    customElements.define('sticky-header', HlHeader);
  }
})();
